
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Receipt, Eye, Pencil, Trash, PlusCircle } from "lucide-react";
import { Category, ExpenseItem, displayDate } from "@/types/ExpenseReport";
import ReceiptUpload from "@/components/ReceiptUpload";
import LoadingSpinner from "@/components/LoadingSpinner";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useState } from "react";
import { format } from "date-fns";

interface ExpenseTableProps {
  expenses: ExpenseItem[] | undefined;
  expensesLoading: boolean;
  canEditReport: boolean;
  receiptUrls: { [key: string]: string };
  handleReceiptUploadComplete: (fileUrl: string, fileName: string) => void;
  handleOpenEditExpenseDialog: (expense: ExpenseItem) => void;
  handleOpenAddExpenseDialog: () => void;
  handleDeleteExpense: (expenseId: string) => void;
  isDeletingExpense: boolean;
  organizationId: string;
}

export const ExpenseTable = ({
  expenses,
  expensesLoading,
  canEditReport,
  receiptUrls,
  handleReceiptUploadComplete,
  handleOpenEditExpenseDialog,
  handleOpenAddExpenseDialog,
  handleDeleteExpense,
  isDeletingExpense,
  organizationId
}: ExpenseTableProps) => {
  const isMobile = useIsMobile();
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedDay, setSelectedDay] = useState(new Date().getDate());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [currentExpense, setCurrentExpense] = useState<ExpenseItem | null>(null);
  
  // Helper function to ensure consistent date display without timezone issues
  const formatDate = (dateString: string): string => {
    // Parse the date string (yyyy-mm-dd) into a Date object
    // This approach avoids timezone issues by explicitly setting the date components
    const [year, month, day] = dateString.split('-').map(num => parseInt(num, 10));
    const date = new Date(year, month - 1, day);
    
    return new Intl.DateTimeFormat('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    }).format(date);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
        <div>
          <CardTitle>Expenses</CardTitle>
          <CardDescription>
            {expenses?.length || 0} expenses in this report
          </CardDescription>
        </div>
        {canEditReport && (
          <Button size="sm" onClick={handleOpenAddExpenseDialog}>
            <PlusCircle className="mr-2 h-4 w-4" />
            Add Expense
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {expensesLoading ? (
          <div className="flex justify-center py-6">
            <LoadingSpinner />
          </div>
        ) : expenses?.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            No expenses added to this report yet.
          </div>
        ) : (
          <div className="overflow-x-auto -mx-4 sm:-mx-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="hidden sm:table-cell">Category</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  {canEditReport && <TableHead className="w-[100px]">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses?.map((expense) => (
                  <TableRow key={expense.id}>
                    <TableCell className="font-medium whitespace-nowrap">
                      {formatDate(expense.date)}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col">
                        <div className="mobile-expense-title">{expense.description}</div>
                        {isMobile && expense.categories?.name && (
                          <div className="mobile-expense-meta">{expense.categories.name}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {expense.categories?.name}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {expense.amount.toFixed(2)} {expense.currency}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap space-x-1">
                        {receiptUrls[expense.id] ? (
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => window.open(receiptUrls[expense.id], '_blank')}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        ) : (
                          canEditReport && (
                            <ReceiptUpload
                              expenseId={expense.id}
                              organizationId={organizationId}
                              onUploadComplete={handleReceiptUploadComplete}
                              isUuid={true}
                              compact={isMobile}
                            >
                              <Button variant="outline" size="icon" className="h-8 w-8">
                                <Receipt className="h-4 w-4" />
                              </Button>
                            </ReceiptUpload>
                          )
                        )}
                        
                        {canEditReport && (
                          <>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => handleOpenEditExpenseDialog(expense)}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8 text-red-500 hover:text-red-700"
                              onClick={() => handleDeleteExpense(expense.id)}
                              disabled={isDeletingExpense}
                            >
                              <Trash className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
